import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { updateCropperValues, setCropperChangedStatus } from '../../../store/actions/cropper.actions'
import CropperOverlay from './CropperOverlay'
import { CROPPER_PRESET_TYPES, mapControls } from '../../../constants/controls'
import { findPresetById, simplePointerEventsData } from '../../../helpers/util'

class Cropper extends Component {
  static propTypes = {
    cropper: PropTypes.object.isRequired,
    canvas: PropTypes.object.isRequired,
    updateCropperValues: PropTypes.func.isRequired,
    setCropperChangedStatus: PropTypes.func.isRequired,
  }

  state = {
    posX: 0,
    posY: 0,
    maxCropperWidth: 0,
    maxCropperHeight: 0,
  }

  componentDidMount() {
    this.init()

    window.addEventListener(simplePointerEventsData.pointerup, this.handleMouseUp)
    window.addEventListener('resize', this.handleResize)
  }

  componentDidUpdate(prevProps) {
    const { canvas } = this.props
    if (
      prevProps.canvas.width !== canvas.width ||
      prevProps.canvas.height !== canvas.height ||
      prevProps.canvas.y !== canvas.y ||
      prevProps.canvas.x !== canvas.x
    ) {
      this.init()
    }
  }

  componentWillUnmount() {
    window.removeEventListener(simplePointerEventsData.pointerup, this.handleMouseUp)
    window.removeEventListener('resize', this.handleResize)
    this.unbindMoveEvents()
  }

  handleMouseUp = () => {
    this.unbindMoveEvents()

    this.setState({ posX: 0, posY: 0, maxCropperWidth: 0, maxCropperHeight: 0 })
  }

  handleResize = () => {
    const {
      updateCropperValues,
      cropper
    } = this.props

    if (cropper.activePresetId) {
      const newCropper = {
        ...cropper,
        activePresetId: 0
      }

      updateCropperValues(newCropper)
    }
  }

  unbindMoveEvents = () => {
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveCropper)
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveLeftTopAngle)
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveLeftBottomAngle)
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveRightTopAngle)
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveRightBottomAngle)
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveLeftOutsideLine)
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveTopOutsideLine)
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveRightOutsideLine)
    window.removeEventListener(simplePointerEventsData.pointermove, this.moveBottomOutsideLine)
  }

  init = () => {
    const { updateCropperValues, cropper, canvas } = this.props
    const newCropper = {
      ...cropper,
      x: canvas.x,
      y: canvas.y,
      width: Math.floor(canvas.width),
      height: Math.floor(canvas.height),
    }

    updateCropperValues(newCropper)
  }

  onCropperDown = (e) => {
    e.stopPropagation()
    window.addEventListener(simplePointerEventsData.pointermove, this.moveCropper)
  }

  moveCropper = (e) => {
    let { posX, posY } = this.state
    const { cropper, canvas } = this.props
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const newCropper = { ...cropper }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    if (posX > clientX) newCropper.x -= posX - clientX
    if (posX < clientX) newCropper.x += clientX - posX
    if (posY > clientY) newCropper.y -= posY - clientY
    if (posY < clientY) newCropper.y += clientY - posY

    if (newCropper.x < canvas.x) newCropper.x = canvas.x
    if (newCropper.x - canvas.x + newCropper.width > canvas.width)
      newCropper.x = canvas.width - newCropper.width + canvas.x
    if (newCropper.y < canvas.y) newCropper.y = canvas.y
    if (newCropper.y - canvas.y + newCropper.height > canvas.height)
      newCropper.y = canvas.height - newCropper.height + canvas.y

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  onLeftOutsideLineDown = (e) => {
    e.stopPropagation()

    const { cropper, canvas } = this.props
    window.addEventListener(simplePointerEventsData.pointermove, this.moveLeftOutsideLine)

    this.setState({
      maxCropperWidth: cropper.x - canvas.x + cropper.width,
      maxCropperHeight: cropper.y - canvas.y + cropper.height,
    })
  }

  onTopOutsideLineDown = (e) => {
    e.stopPropagation()

    const { cropper, canvas } = this.props
    window.addEventListener(simplePointerEventsData.pointermove, this.moveTopOutsideLine)

    this.setState({
      maxCropperWidth: cropper.x - canvas.x + cropper.width,
      maxCropperHeight: cropper.y - canvas.y + cropper.height,
    })
  }

  onRightOutsideLineDown = (e) => {
    e.stopPropagation()

    const { cropper, canvas } = this.props
    window.addEventListener(simplePointerEventsData.pointermove, this.moveRightOutsideLine)

    this.setState({
      maxCropperWidth: cropper.x - canvas.x + cropper.width,
      maxCropperHeight: cropper.y - canvas.y + cropper.height,
    })
  }

  onBottomOutsideLineDown = (e) => {
    e.stopPropagation()

    const { cropper, canvas } = this.props
    window.addEventListener(simplePointerEventsData.pointermove, this.moveBottomOutsideLine)

    this.setState({
      maxCropperWidth: cropper.x - canvas.x + cropper.width,
      maxCropperHeight: cropper.y - canvas.y + cropper.height,
    })
  }

  moveLeftOutsideLine = (e) => {
    let { maxCropperWidth, posX, posY } = this.state
    const { cropper, canvas } = this.props
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const newCropper = { ...cropper, activePresetId: 0 }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    if (posX > clientX && newCropper.x > canvas.x) {
      newCropper.x -= posX - clientX
      newCropper.width += posX - clientX
    }
    if (posX < clientX && newCropper.width > 1) {
      newCropper.x += clientX - posX
      newCropper.width -= clientX - posX
    }
    // if (posY > clientY && newCropper.y > y) {
    //   newCropper.y -= posY - clientY
    //   newCropper.height += posY - clientY
    // }
    // if (posY < clientY && newCropper.height > 1) {
    //   newCropper.y += clientY - posY
    //   newCropper.height -= clientY - posY
    // }

    if (newCropper.x < canvas.x) {
      newCropper.x = canvas.x
      newCropper.width = maxCropperWidth
    }
    if (newCropper.width < 1) {
      newCropper.x = canvas.x + maxCropperWidth - 1
      newCropper.width = 1
    }
    // if (newCropper.y < y) {
    //   newCropper.y = y
    //   newCropper.height = maxCropperHeight
    // }
    // if (newCropper.height < 1) {
    //   newCropper.y = y + maxCropperHeight - 1
    //   newCropper.height = 1
    // }

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  moveTopOutsideLine = (e) => {
    let { maxCropperHeight, posX, posY } = this.state
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const { cropper, canvas } = this.props
    const newCropper = { ...cropper, activePresetId: 0 }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    // if (posX > clientX && newCropper.x > x) {
    //   newCropper.x -= posX - clientX
    //   newCropper.width += posX - clientX
    // }
    // if (posX < clientX && newCropper.width > 1) {
    //   newCropper.x += clientX - posX
    //   newCropper.width -= clientX - posX
    // }
    if (posY > clientY && newCropper.y > canvas.y) {
      newCropper.y -= posY - clientY
      newCropper.height += posY - clientY
    }
    if (posY < clientY && newCropper.height > 1) {
      newCropper.y += clientY - posY
      newCropper.height -= clientY - posY
    }

    // if (newCropper.x < x) {
    //   newCropper.x = x
    //   newCropper.width = maxCropperWidth
    // }
    // if (newCropper.width < 1) {
    //   newCropper.x = x + maxCropperWidth - 1
    //   newCropper.width = 1
    // }
    if (newCropper.y < canvas.y) {
      newCropper.y = canvas.y
      newCropper.height = maxCropperHeight
    }
    if (newCropper.height < 1) {
      newCropper.y = canvas.y + maxCropperHeight - 1
      newCropper.height = 1
    }

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  moveRightOutsideLine = (e) => {
    let { posX, posY } = this.state
    const { cropper, canvas } = this.props
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const newCropper = { ...cropper, activePresetId: 0 }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    if (posX > clientX) newCropper.width -= posX - clientX
    if (posX < clientX) newCropper.width += clientX - posX
    // if (posY > clientY && newCropper.y > y) {
    //   newCropper.y -= posY - clientY
    //   newCropper.height += posY - clientY
    // }
    // if (posY < clientY && newCropper.height > 1) {
    //   newCropper.y += clientY - posY
    //   newCropper.height -= clientY - posY
    // }

    if (newCropper.x - canvas.x + newCropper.width > canvas.width)
      newCropper.width = canvas.width - (newCropper.x - canvas.x)
    if (newCropper.width < 1) newCropper.width = 1
    // if (newCropper.y < y) {
    //   newCropper.y = y
    //   newCropper.height = maxCropperHeight
    // }
    // if (newCropper.height < 1) {
    //   newCropper.y = y + maxCropperHeight - 1
    //   newCropper.height = 1
    // }

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  moveBottomOutsideLine = (e) => {
    let { posX, posY } = this.state
    const { cropper, canvas } = this.props
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const newCropper = { ...cropper, activePresetId: 0 }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    // if (posX > clientX) newCropper.width -= posX - clientX
    // if (posX < clientX) newCropper.width += clientX - posX
    if (posY > clientY) newCropper.height += clientY - posY
    if (posY < clientY) newCropper.height -= posY - clientY

    // if (newCropper.x - x + newCropper.width > width) newCropper.width = width - (newCropper.x - x)
    // if (newCropper.width < 1) newCropper.width = 1
    if (newCropper.y - canvas.y + newCropper.height > canvas.height)
      newCropper.height = canvas.height - (newCropper.y - canvas.y)
    if (newCropper.height < 1) newCropper.height = 1

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  onLeftTopAngleDown = (e) => {
    e.stopPropagation()

    const { cropper, canvas } = this.props
    window.addEventListener(simplePointerEventsData.pointermove, this.moveLeftTopAngle)

    this.setState({
      maxCropperWidth: cropper.x - canvas.x + cropper.width,
      maxCropperHeight: cropper.y - canvas.y + cropper.height,
    })
  }

  onLeftBottomAngleDown = (e) => {
    e.stopPropagation()

    const { cropper, canvas } = this.props
    window.addEventListener(simplePointerEventsData.pointermove, this.moveLeftBottomAngle)

    this.setState({
      maxCropperWidth: cropper.x - canvas.x + cropper.width,
      maxCropperHeight: cropper.y - canvas.y + cropper.height,
    })
  }

  onRightTopAngleDown = (e) => {
    e.stopPropagation()

    const { cropper, canvas } = this.props
    window.addEventListener(simplePointerEventsData.pointermove, this.moveRightTopAngle)

    this.setState({
      maxCropperWidth: cropper.x - canvas.x + cropper.width,
      maxCropperHeight: cropper.y - canvas.y + cropper.height,
    })
  }

  onRightBottomAngleDown = (e) => {
    e.stopPropagation()

    const { cropper, canvas } = this.props
    window.addEventListener(simplePointerEventsData.pointermove, this.moveRightBottomAngle)

    this.setState({
      maxCropperWidth: cropper.x - canvas.x + cropper.width,
      maxCropperHeight: cropper.y - canvas.y + cropper.height,
    })
  }

  moveLeftTopAngle = (e) => {
    let { maxCropperWidth, maxCropperHeight, posX, posY } = this.state
    const { cropper, canvas } = this.props
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const ratio = cropper.width / cropper.height
    const selectedPreset = findPresetById(mapControls.cropper, cropper.activePresetId)
    const newCropper = {
      ...cropper,
      activePresetId: selectedPreset.type === CROPPER_PRESET_TYPES.SIZE ? 0 : cropper.activePresetId
    }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    if (!newCropper.locked) {
      if (posX > clientX && newCropper.x > canvas.x) {
        newCropper.x -= posX - clientX
        newCropper.width += posX - clientX
      }
      if (posX < clientX && newCropper.width > 1) {
        newCropper.x += clientX - posX
        newCropper.width -= clientX - posX
      }
    }
    if (posY > clientY && newCropper.y > canvas.y) {
      if (!newCropper.locked) {
        newCropper.y -= posY - clientY
        newCropper.height += posY - clientY
      }
      if (newCropper.locked && newCropper.x > canvas.x) {
        newCropper.y -= posY - clientY
        newCropper.height += posY - clientY
        newCropper.x -= (posY - clientY) * ratio
        newCropper.width += (posY - clientY) * ratio
      }
    }
    if (posY < clientY) {
      newCropper.y += clientY - posY
      newCropper.height -= clientY - posY
      if (newCropper.locked) {
        newCropper.x += (clientY - posY) * ratio
        newCropper.width -= (clientY - posY) * ratio
      }
    }

    if (newCropper.x < canvas.x) {
      newCropper.x = canvas.x
      newCropper.width = maxCropperWidth
      if (cropper.locked) {
        const cropperHeight = newCropper.width / ratio
        newCropper.y = canvas.y + (canvas.height - (canvas.height - maxCropperHeight + cropperHeight))
        newCropper.height = cropperHeight
      }
    }
    if (newCropper.width < 1) {
      newCropper.x = canvas.x + maxCropperWidth - 1
      newCropper.width = 1
    }
    if (newCropper.y < canvas.y) {
      newCropper.y = canvas.y
      newCropper.height = maxCropperHeight
      if (cropper.locked) {
        const cropperWidth = newCropper.height * ratio
        newCropper.x = canvas.x + (canvas.width - (canvas.width - maxCropperWidth + cropperWidth))
        newCropper.width = cropperWidth
      }
    }
    if (newCropper.height < 1) {
      newCropper.y = canvas.y + maxCropperHeight - 1
      newCropper.height = 1
    }

    if (newCropper.width < 1 * ratio && ratio > 1 && newCropper.locked) {
      newCropper.x = canvas.x + maxCropperWidth - 1 * ratio
      newCropper.width = 1 * ratio
      newCropper.y = canvas.y + maxCropperHeight - 1
      newCropper.height = 1
    }
    if (newCropper.height < 1 / ratio && ratio < 1 && newCropper.locked) {
      newCropper.y = canvas.y + maxCropperHeight - 1 / ratio
      newCropper.height = 1 / ratio
      newCropper.x = canvas.x + maxCropperWidth - 1
      newCropper.width = 1
    }

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  moveLeftBottomAngle = (e) => {
    let { maxCropperWidth, posX, posY } = this.state
    const { cropper, canvas } = this.props
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const ratio = cropper.width / cropper.height
    const selectedPreset = findPresetById(mapControls.cropper, cropper.activePresetId)
    const newCropper = {
      ...cropper,
      activePresetId: selectedPreset.type === CROPPER_PRESET_TYPES.SIZE ? 0 : cropper.activePresetId
    }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    if (posX > clientX && newCropper.x > canvas.x) {
      if (!newCropper.locked) {
        newCropper.x -= posX - clientX
        newCropper.width += posX - clientX
      }
      if (newCropper.locked && newCropper.y - canvas.y + newCropper.height < canvas.height) {
        newCropper.x -= posX - clientX
        newCropper.width += posX - clientX
        newCropper.height += (posX - clientX) / ratio
      }
    }
    if (posX < clientX) {
      newCropper.x += clientX - posX
      newCropper.width -= clientX - posX
      if (newCropper.locked) newCropper.height -= (clientX - posX) / ratio
    }
    if (!newCropper.locked) {
      if (posY > clientY) newCropper.height += clientY - posY
      if (posY < clientY) newCropper.height -= posY - clientY
    }

    if (newCropper.x < canvas.x) {
      newCropper.x = canvas.x
      newCropper.width = maxCropperWidth
      if (cropper.locked) newCropper.height = newCropper.width / ratio
    }
    if (newCropper.width < 1) {
      newCropper.x = canvas.x + maxCropperWidth - 1
      newCropper.width = 1
    }
    if (newCropper.y - canvas.y + newCropper.height > canvas.height) {
      newCropper.height = Math.floor(canvas.height) - (newCropper.y - canvas.y)
      if (newCropper.locked) {
        const cropperWidth = newCropper.height * ratio
        newCropper.x = canvas.x + (canvas.width - (canvas.width - maxCropperWidth + cropperWidth))
        newCropper.width = cropperWidth
      }
    }
    if (newCropper.height < 1) newCropper.height = 1

    if (newCropper.width < 1 * ratio && ratio > 1 && newCropper.locked) {
      newCropper.x = canvas.x + maxCropperWidth - 1 * ratio
      newCropper.width = 1 * ratio
      newCropper.height = 1
    }
    if (newCropper.height < 1 / ratio && ratio < 1 && newCropper.locked) {
      newCropper.x = canvas.x + maxCropperWidth - 1
      newCropper.height = 1 / ratio
      newCropper.width = 1
    }

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  moveRightTopAngle = (e) => {
    let { maxCropperHeight, posX, posY } = this.state
    const { cropper, canvas } = this.props
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const ratio = cropper.width / cropper.height
    const selectedPreset = findPresetById(mapControls.cropper, cropper.activePresetId)
    const newCropper = {
      ...cropper,
      activePresetId: selectedPreset.type === CROPPER_PRESET_TYPES.SIZE ? 0 : cropper.activePresetId
    }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    if (!newCropper.locked) {
      if (posX > clientX) newCropper.width -= posX - clientX
      if (posX < clientX) newCropper.width += clientX - posX
    }
    if (posY > clientY && newCropper.y > canvas.y) {
      if (!newCropper.locked) {
        newCropper.y -= posY - clientY
        newCropper.height += posY - clientY
      }
      if (newCropper.locked && newCropper.x - canvas.x + newCropper.width < canvas.width) {
        newCropper.y -= posY - clientY
        newCropper.height += posY - clientY
        newCropper.width += (posY - clientY) * ratio
      }
    }
    if (posY < clientY) {
      newCropper.y += clientY - posY
      newCropper.height -= clientY - posY
      if (newCropper.locked) newCropper.width -= (clientY - posY) * ratio
    }

    if (newCropper.x - canvas.x + newCropper.width > canvas.width) {
      newCropper.width = canvas.width - (newCropper.x - canvas.x)
      if (newCropper.locked) {
        const cropperHeight = newCropper.width / ratio
        newCropper.y = canvas.y + (canvas.height - (canvas.height - maxCropperHeight + cropperHeight))
        newCropper.height = cropperHeight
      }
    }
    if (newCropper.width < 1) newCropper.width = 1
    if (newCropper.y < canvas.y) {
      newCropper.y = canvas.y
      newCropper.height = maxCropperHeight
    }
    if (newCropper.height < 1) {
      newCropper.y = canvas.y + maxCropperHeight - 1
      newCropper.height = 1
    }

    if (newCropper.width < 1 * ratio && ratio > 1 && newCropper.locked) {
      newCropper.width = 1 * ratio
      newCropper.y = canvas.y + maxCropperHeight - 1
      newCropper.height = 1
    }
    if (newCropper.height < 1 / ratio && ratio < 1 && newCropper.locked) {
      newCropper.y = canvas.y + maxCropperHeight - 1 / ratio
      newCropper.height = 1 / ratio
      newCropper.width = 1
    }

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  moveRightBottomAngle = (e) => {
    let { posX, posY } = this.state
    const { cropper, canvas } = this.props
    const clientX = simplePointerEventsData.isTouch ? e.touches[0].clientX : e.clientX
    const clientY = simplePointerEventsData.isTouch ? e.touches[0].clientY : e.clientY
    const ratio = cropper.width / cropper.height
    const selectedPreset = findPresetById(mapControls.cropper, cropper.activePresetId)
    const newCropper = {
      ...cropper,
      activePresetId: selectedPreset.type === CROPPER_PRESET_TYPES.SIZE ? 0 : cropper.activePresetId
    }
    if (posX === 0) posX = clientX
    if (posY === 0) posY = clientY

    if (posX > clientX) {
      newCropper.width -= posX - clientX
      if (newCropper.locked) newCropper.height -= (posX - clientX) / ratio
    }
    if (posX < clientX) {
      newCropper.width += clientX - posX
      if (newCropper.locked) newCropper.height += (clientX - posX) / ratio
    }
    if (!newCropper.locked) {
      if (posY > clientY) newCropper.height += clientY - posY
      if (posY < clientY) newCropper.height -= posY - clientY
    }

    if (newCropper.x - canvas.x + newCropper.width > canvas.width) {
      newCropper.width = canvas.width - (newCropper.x - canvas.x)
      if (newCropper.locked) {
        const cropperHeight = newCropper.width / ratio
        newCropper.height = cropperHeight
      }
    }
    if (newCropper.width < 1) newCropper.width = 1
    if (newCropper.y - canvas.y + newCropper.height > canvas.height) {
      newCropper.height = Math.floor(canvas.height) - (newCropper.y - canvas.y)
      if (newCropper.locked) {
        const cropperWidth = newCropper.height * ratio
        newCropper.width = cropperWidth
      }
    }
    if (newCropper.height < 1) newCropper.height = 1

    if (newCropper.width < 1 * ratio && ratio > 1 && newCropper.locked) {
      newCropper.width = 1 * ratio
      newCropper.height = 1
    }
    if (newCropper.height < 1 / ratio && ratio < 1 && newCropper.locked) {
      newCropper.height = 1 / ratio
      newCropper.width = 1
    }

    this.setState({ posX: clientX, posY: clientY })
    this.updateCropper(newCropper)
  }

  updateCropper = (newCropper) => {
    const { updateCropperValues, setCropperChangedStatus } = this.props

    updateCropperValues(newCropper)
    if (!newCropper.changed) setCropperChangedStatus(true)
  }

  render() {
    const {
      cropper,
      canvas
    } = this.props
    const cropperClass =
        cropper.width <= 24 || cropper.height <= 24
          ? 'cropper_smaller'
          : cropper.width < 100 || cropper.height < 100
            ? 'cropper_small'
            : ''

    return (
      <React.Fragment>
        <CropperOverlay
          canvas={canvas}
          cropper={cropper}
        />
        <div
          className={`cropper ${cropperClass}`}
          style={{
            left: `${cropper.x}px`,
            top: `${cropper.y}px`,
            width: `${cropper.width}px`,
            height: `${cropper.height}px`,
          }}
          onMouseDown={this.onCropperDown}
          onTouchStart={this.onCropperDown}
        >
          <div
            className="cropper__outside-line cropper__outside-line_left"
            onMouseDown={this.onLeftOutsideLineDown}
            onTouchStart={this.onLeftOutsideLineDown}
          />
          <div
            className="cropper__outside-line cropper__outside-line_top"
            onMouseDown={this.onTopOutsideLineDown}
            onTouchStart={this.onTopOutsideLineDown}
          />
          <div
            className="cropper__outside-line cropper__outside-line_right"
            onMouseDown={this.onRightOutsideLineDown}
            onTouchStart={this.onRightOutsideLineDown}
          />
          <div
            className="cropper__outside-line cropper__outside-line_bottom"
            onMouseDown={this.onBottomOutsideLineDown}
            onTouchStart={this.onBottomOutsideLineDown}/>
          <div className="cropper__inside-line cropper__inside-line_left" />
          <div className="cropper__inside-line cropper__inside-line_top" />
          <div className="cropper__inside-line cropper__inside-line_right" />
          <div className="cropper__inside-line cropper__inside-line_bottom" />
          <div
            className="cropper__angle cropper__angle_left-top"
            onMouseDown={this.onLeftTopAngleDown}
            onTouchStart={this.onLeftTopAngleDown}
          />
          <div
            className="cropper__angle cropper__angle_right-top"
            onMouseDown={this.onRightTopAngleDown}
            onTouchStart={this.onRightTopAngleDown}
          />
          <div
            className="cropper__angle cropper__angle_right-bottom"
            onMouseDown={this.onRightBottomAngleDown}
            onTouchStart={this.onRightBottomAngleDown}
          />
          <div
            className="cropper__angle cropper__angle_left-bottom"
            onMouseDown={this.onLeftBottomAngleDown}
            onTouchStart={this.onLeftBottomAngleDown}
          />
          <div className="cropper__handle cropper__handle_top" />
          <div className="cropper__handle cropper__handle_right" />
          <div className="cropper__handle cropper__handle_bottom" />
          <div className="cropper__handle cropper__handle_left" />
        </div>
      </React.Fragment>
    )
  }
}

const mapStateToProps = ({ cropperStore: { cropper }, canvasStore: { canvas } }) => ({ cropper, canvas })

const mapDispatchToProps = (dispatch) => ({
  updateCropperValues: (values) => dispatch(updateCropperValues(values)),
  setCropperChangedStatus: (status) => dispatch(setCropperChangedStatus(status)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Cropper)
